import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  Minus,
  MoreHorizontal,
  Plus,
  Redo2,
  Star,
  Tag,
  Truck,
  Type,
  Undo2,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Product } from '@/api/products';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import {
  useCanvasStateManager,
  useCanUndo,
  useCanRedo,
} from '@/stores/canvasStateManager';
import type { DirectSaleTool } from './DirectSaleDesignToolbar';
import { DirectSaleMethodPicker } from './DirectSaleMethodPicker';
import { DirectSaleSheet } from './DirectSaleSheet';
import { DirectSaleCatalog } from './DirectSaleCatalog';
import { DirectSaleMockupPreview } from './DirectSaleMockupPreview';
import { useDirectSaleCheckout } from './useDirectSaleCheckout';
import { formatMoney } from './format-money';
import { DirectSaleMoreSheet } from './DirectSaleMoreSheet';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { sizesForColor } from './direct-sale-sizes';
import { describeQuoteError } from './quote-error';

/** The phone's own sheets; the tool panels are tracked by the design step. */
export type DirectSaleMobileSheet =
  | 'add'
  | 'color'
  | 'info'
  | 'buy'
  | 'products';

/** Mirrors DIRECT_SALE_MAX_QUANTITY on the API. */
const MAX_QUANTITY = 99;

const floating =
  'border border-line bg-paper shadow-[0_2px_8px_rgba(13,13,13,0.06)]';

/**
 * What floats over the canvas on a phone: undo/redo, the product colour and
 * the details menu at the top. Always there, whatever the customer is
 * doing. The view switch is not here: it sits under the product in the
 * bottom stack (DirectSalePositionPills, variant 'phone').
 */
export function DirectSaleMobileOverlay({
  product,
  onOpenSheet,
}: {
  product: Product;
  onOpenSheet: (sheet: DirectSaleMobileSheet) => void;
}) {
  const { t } = useTranslation();
  const colorId = useDirectSaleStore((s) => s.colorId);
  const { undo, redo } = useCanvasStateManager();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  const color = product.colors?.find((c) => c.id === colorId) ?? null;

  return (
    <div className="pointer-events-none absolute inset-0 z-10 min-[981px]:hidden">
      <div className="pointer-events-auto absolute inset-x-2.5 top-3 flex items-center justify-between">
        <div className={cn('flex h-11 rounded-full', floating)}>
          <button
            type="button"
            aria-label={t('directSale.design.undo')}
            onClick={() => void undo()}
            disabled={!canUndo}
            className={cn(
              'flex h-11 w-11 items-center justify-center',
              canUndo ? 'text-ink' : 'text-ink-3'
            )}
          >
            <Undo2 className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label={t('directSale.design.redo')}
            onClick={() => void redo()}
            disabled={!canRedo}
            className={cn(
              'flex h-11 w-11 items-center justify-center',
              canRedo ? 'text-ink' : 'text-ink-3'
            )}
          >
            <Redo2 className="h-5 w-5" />
          </button>
        </div>

        {(product.colors?.length ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => onOpenSheet('color')}
            className={cn(
              'flex h-11 items-center gap-2 rounded-full pl-2 pr-4 text-[15px] font-semibold text-ink',
              floating
            )}
          >
            <span
              className="h-7 w-7 rounded-full border border-black/25"
              style={{ backgroundColor: color?.hexCode || '#e5e7eb' }}
            />
            {t('directSale.design.color')}
            <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
          </button>
        )}

        <button
          type="button"
          aria-label={t('directSale.mobile.moreOptions')}
          onClick={() => onOpenSheet('info')}
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full text-ink',
            floating
          )}
        >
          <MoreHorizontal className="h-[22px] w-[22px]" />
        </button>
      </div>
    </div>
  );
}

/**
 * The phone's bottom card — products, add, buy — and the sheets it opens.
 * Tools sit where the thumb is; the price is always on the buy button.
 */
export function DirectSaleMobileBar({
  product,
  sizes,
  sheet,
  onSheetChange,
  onOpenTool,
  hidden,
  above,
}: {
  product: Product;
  sizes: { id: string; name: string }[];
  sheet: DirectSaleMobileSheet | null;
  onSheetChange: (sheet: DirectSaleMobileSheet | null) => void;
  onOpenTool: (tool: DirectSaleTool) => void;
  /** A tool panel owns the bottom of the screen while it is open. */
  hidden: boolean;
  /**
   * Rides on top of the card: the view thumbnails. In this stack rather than
   * in the canvas box, so they sit right under the product, are never
   * covered by the tiles — and the product itself never has to move.
   */
  above?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const colorId = useDirectSaleStore((s) => s.colorId);
  const setColor = useDirectSaleStore((s) => s.setColor);
  const setQuantity = useDirectSaleStore((s) => s.setQuantity);
  const quantities = useDirectSaleStore((s) => s.quantities);
  const [showPriceDetails, setShowPriceDetails] = useState(false);
  const selectProduct = useDirectSaleStore((s) => s.selectProduct);
  // The product the customer tapped while a design exists: switching
  // discards it (R21), so they confirm first.
  const [pendingSwitch, setPendingSwitch] = useState<Product | null>(null);
  const [catalogCount, setCatalogCount] = useState<number | null>(null);
  const hasDecoration = useDirectSaleStore(
    (s) => s.decoratedPositions.length > 0
  );

  const { quote, gallery, handleCheckout, isSubmitting, checkoutError } =
    useDirectSaleCheckout(product, { enabled: hasDecoration });
  const money = (amount: string | number) =>
    formatMoney(amount, quote.data?.currency ?? 'EUR', i18n.language);

  const color = product.colors?.find((c) => c.id === colorId) ?? null;
  // The colour thumbs show the view the customer is looking at.
  const activePosition = useProductSelectionStore((s) => s.activePosition);
  const thumbSlug =
    product.positions?.find((p) => p.slug === activePosition)?.slug ??
    product.positions?.[0]?.slug ??
    null;
  /** Pieces in the size sheet; nothing chosen shows the item price only. */
  const pieces = Object.values(quantities).reduce((sum, n) => sum + n, 0);
  // Tiles are open on an empty product: the first thing a customer needs is
  // a way to start, not a button that reveals one.
  const showTiles = sheet === 'add' || (!hasDecoration && sheet === null);

  const priceLabel =
    hasDecoration && quote.data
      ? money(quote.data.total)
      : product.recommendedSalesPrice !== undefined &&
          product.recommendedSalesPrice !== null
        ? t('directSale.product.priceFrom', {
            price: money(product.recommendedSalesPrice),
          })
        : '';

  const tile =
    'flex h-[68px] flex-col items-center justify-center gap-1 rounded-card bg-paper-2 text-[13px] font-bold text-ink';
  const barButton =
    'flex h-[72px] flex-col items-center justify-center gap-1 text-[13px] font-bold text-ink';

  const pickTool = (tool: DirectSaleTool) => {
    onSheetChange(null);
    onOpenTool(tool);
  };

  return (
    <div className="min-[981px]:hidden">
      {!hidden && sheet !== 'color' && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex flex-col gap-2 px-3 pb-[max(env(safe-area-inset-bottom),14px)] [&>*]:pointer-events-auto">
          {above}
          {showTiles && (
            <div
              className={cn(
                'flex flex-col gap-2.5 rounded-[18px] p-3',
                floating
              )}
            >
              <div className="text-center font-display text-lg font-bold text-primary-700">
                {t('directSale.mobile.startDesigning')}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  className={tile}
                  onClick={() => pickTool('upload')}
                >
                  <Upload className="h-[22px] w-[22px]" />
                  {t('directSale.mobile.uploadShort')}
                </button>
                <button
                  type="button"
                  className={tile}
                  onClick={() => pickTool('text')}
                >
                  <Type className="h-[22px] w-[22px]" />
                  {t('directSale.mobile.textShort')}
                </button>
                <button
                  type="button"
                  className={tile}
                  onClick={() => pickTool('motifs')}
                >
                  <Star className="h-[22px] w-[22px]" />
                  {t('directSale.design.motifs')}
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <div
              className={cn('grid flex-1 grid-cols-2 rounded-[18px]', floating)}
            >
              <button
                type="button"
                className={barButton}
                onClick={() => onSheetChange('products')}
              >
                <Tag className="h-[22px] w-[22px]" />
                {t('directSale.mobile.products')}
              </button>
              <button
                type="button"
                aria-expanded={showTiles}
                className={barButton}
                onClick={() => onSheetChange(sheet === 'add' ? null : 'add')}
              >
                <Plus className="h-[22px] w-[22px]" strokeWidth={2.2} />
                {t('directSale.mobile.add')}
              </button>
            </div>
            <button
              type="button"
              onClick={() => onSheetChange(hasDecoration ? 'buy' : 'add')}
              className={cn(
                'flex h-[74px] w-[138px] flex-col items-center justify-center gap-0.5 rounded-[18px] border-2',
                hasDecoration
                  ? 'border-primary bg-primary text-primary-fg'
                  : 'border-line bg-paper text-ink-3'
              )}
            >
              <span
                className={cn(
                  'font-display text-[17px] font-bold',
                  quote.isPlaceholderData && 'opacity-60'
                )}
              >
                {priceLabel}
              </span>
              <span className="text-[13px] font-bold">
                {t('directSale.mobile.buy')}
              </span>
            </button>
          </div>
        </div>
      )}

      {sheet === 'color' && (
        <DirectSaleSheet
          title={t('directSale.mobile.productColor')}
          subtitle={color?.name}
          onClose={() => onSheetChange(null)}
        >
          {/* The product in each colour, not a swatch: a hex dot says little
              about a heathered grey or how a dark shirt carries the design. */}
          <div className="grid grid-cols-4 gap-x-1 gap-y-2 px-3 pb-2 pt-1.5">
            {product.colors?.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-label={c.name}
                aria-pressed={c.id === colorId}
                onClick={() =>
                  setColor(
                    c.id,
                    sizesForColor(product, c.id).map((size) => size.id)
                  )
                }
                className="flex flex-col items-center gap-1"
              >
                <span
                  className={cn(
                    'block rounded-[12px] border-2 bg-[rgb(var(--color-canvas-bg))] p-0.5',
                    c.id === colorId ? 'border-primary' : 'border-transparent'
                  )}
                >
                  {thumbSlug ? (
                    <DirectSaleMockupPreview
                      product={product}
                      positionSlug={thumbSlug}
                      colorId={c.id}
                      designDataUrl={null}
                      fit={1}
                      lazyLoad
                      className="h-[72px] w-[72px]"
                    />
                  ) : (
                    <span
                      className="block h-[72px] w-[72px] rounded-[10px]"
                      style={{ backgroundColor: c.hexCode || '#e5e7eb' }}
                    />
                  )}
                </span>
                <span className="w-full truncate text-center text-[11px] text-ink-2">
                  {c.name}
                </span>
              </button>
            ))}
          </div>
          <DirectSaleMethodPicker product={product} className="px-4 pb-3" />
        </DirectSaleSheet>
      )}

      {sheet === 'info' && (
        <DirectSaleMoreSheet
          product={product}
          sizes={sizes}
          onClose={() => onSheetChange(null)}
        />
      )}

      {sheet === 'buy' && (
        <DirectSaleSheet
          title={t('directSale.mobile.chooseSizeQty')}
          modal
          tall
          onClose={() => onSheetChange(null)}
        >
          {/* Size and quantity after Spreadshirt: one row per size with a
              stepper, the money and the button pinned below the list. */}
          <div className="flex h-full flex-col">
            <div className="flex items-center gap-3 px-[18px] pb-2">
              {gallery[0]?.slug && (
                <DirectSaleMockupPreview
                  product={product}
                  positionSlug={gallery[0].slug}
                  designDataUrl={gallery[0].preview}
                  fit={1}
                  className="h-[64px] w-[58px] flex-none rounded-[10px] bg-[rgb(var(--color-canvas-bg))]"
                />
              )}
              <p className="text-sm text-ink-2">
                {[product.name, color?.name].filter(Boolean).join(' · ')}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {sizes.map((size) => {
                const count = quantities[size.id] ?? 0;
                return (
                  <div
                    key={size.id}
                    className="flex h-[60px] items-center justify-between border-t border-line px-[18px]"
                  >
                    <span className="text-lg font-bold text-ink">
                      {size.name}
                    </span>
                    <div
                      role="group"
                      aria-label={size.name}
                      className="flex h-11 items-stretch overflow-hidden rounded-[10px] border border-line"
                    >
                      <button
                        type="button"
                        aria-label={t('directSale.mobile.fewer')}
                        disabled={count === 0}
                        onClick={() => setQuantity(size.id, count - 1)}
                        className="flex w-12 items-center justify-center text-ink disabled:text-ink-3"
                      >
                        <Minus className="h-5 w-5" strokeWidth={2.2} />
                      </button>
                      <output
                        aria-live="polite"
                        className="flex w-12 items-center justify-center border-x border-line text-lg font-semibold text-ink"
                      >
                        {count}
                      </output>
                      <button
                        type="button"
                        aria-label={t('directSale.mobile.more')}
                        disabled={count >= MAX_QUANTITY}
                        onClick={() => setQuantity(size.id, count + 1)}
                        className="flex w-12 items-center justify-center text-ink disabled:text-ink-3"
                      >
                        <Plus className="h-5 w-5" strokeWidth={2.2} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex-none border-t border-line">
              <div className="flex items-center justify-center gap-2 bg-paper-2 px-2 py-1.5 text-[13px] text-ink">
                <Truck className="h-[18px] w-[18px]" />
                {t('directSale.checkout.trustShippingTitle')}
              </div>

              {quote.isError && (
                <p className="px-[18px] pt-3 text-sm text-danger">
                  {describeQuoteError(
                    quote.error,
                    t('directSale.checkout.priceFailed')
                  )}
                </p>
              )}
              {quote.data && (
                <>
                  <div className="flex items-center justify-between px-[18px] pt-3 text-sm text-ink">
                    <span>
                      {t('directSale.checkout.itemPrice')}:{' '}
                      {money(
                        quote.data.items[0]?.unitPrice ?? quote.data.total
                      )}
                    </span>
                    <button
                      type="button"
                      aria-expanded={showPriceDetails}
                      onClick={() => setShowPriceDetails((v) => !v)}
                      className="flex items-center gap-1 font-semibold text-ink underline underline-offset-4"
                    >
                      {t('directSale.checkout.priceDetails')}
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 transition-transform',
                          showPriceDetails && 'rotate-90'
                        )}
                      />
                    </button>
                  </div>
                  {showPriceDetails && (
                    <div className="flex flex-col gap-1 px-[18px] pt-2 text-[13px] text-ink-2">
                      <div className="flex justify-between">
                        <span>{product.name}</span>
                        <span>{money(quote.data.basePrice)}</span>
                      </div>
                      {quote.data.positions.map((line) => (
                        <div
                          key={line.positionId}
                          className="flex justify-between"
                        >
                          <span>
                            {t('directSale.checkout.printArea', {
                              position: line.positionName,
                            })}
                          </span>
                          <span>{money(line.amount)}</span>
                        </div>
                      ))}
                      {pieces > 0 &&
                        quote.data.items.map((item) => (
                          <div
                            key={item.sizeId ?? 'default'}
                            className="flex justify-between"
                          >
                            <span>
                              {item.quantity} × {item.sizeName ?? ''}{' '}
                              {money(item.unitPrice)}
                            </span>
                            <span>{money(item.amount)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 py-3 pl-[18px] pr-3.5">
                    <div className="flex flex-1 flex-col">
                      <span
                        className={cn(
                          'font-display text-2xl font-bold text-ink',
                          quote.isPlaceholderData && 'opacity-60'
                        )}
                      >
                        {money(pieces > 0 ? quote.data.total : 0)}
                      </span>
                      <span className="text-xs text-ink-3">
                        {t('directSale.checkout.vatShort')}
                      </span>
                    </div>
                    <button
                      type="button"
                      disabled={
                        pieces === 0 || !quote.isSuccess || isSubmitting
                      }
                      aria-busy={isSubmitting}
                      onClick={() => void handleCheckout()}
                      className="h-[54px] rounded-[12px] bg-primary px-5 text-[15px] font-bold text-primary-fg disabled:bg-paper-3 disabled:text-ink-2"
                    >
                      {isSubmitting
                        ? t('directSale.checkout.committing')
                        : t('directSale.checkout.addToCartLong')}
                    </button>
                  </div>
                  {checkoutError && (
                    <p
                      role="alert"
                      className="px-[18px] pb-3 text-sm text-danger"
                    >
                      {checkoutError}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </DirectSaleSheet>
      )}
      {sheet === 'products' && (
        <DirectSaleSheet
          title={t('directSale.mobile.chooseProduct')}
          subtitle={catalogCount === null ? null : `(${catalogCount})`}
          modal
          tall
          onClose={() => onSheetChange(null)}
        >
          <div className="px-4 pb-6 pt-1">
            <DirectSaleCatalog
              selectedProductId={product.id}
              selectedLabel={t('directSale.mobile.current')}
              onCount={setCatalogCount}
              onSelect={(picked) => {
                if (picked.id === product.id) {
                  onSheetChange(null);
                } else if (hasDecoration) {
                  setPendingSwitch(picked);
                } else {
                  selectProduct(picked.id);
                }
              }}
            />
          </div>
        </DirectSaleSheet>
      )}

      {pendingSwitch && (
        <div
          role="alertdialog"
          aria-label={t('directSale.mobile.switchTitle', {
            name: pendingSwitch.name,
          })}
          className="fixed inset-0 z-50 flex items-end bg-black/50"
        >
          <div className="flex w-full flex-col gap-2.5 rounded-t-[20px] bg-paper px-[18px] pb-[max(env(safe-area-inset-bottom),20px)] pt-5">
            <h2 className="font-display text-xl font-bold text-ink">
              {t('directSale.mobile.switchTitle', { name: pendingSwitch.name })}
            </h2>
            <p className="text-[15px] leading-relaxed text-ink-2">
              {t('directSale.mobile.switchBody')}
            </p>
            <div className="grid grid-cols-2 gap-2.5 pt-1.5">
              <button
                type="button"
                onClick={() => setPendingSwitch(null)}
                className="h-[52px] rounded-[12px] border border-line text-[15px] font-bold text-ink"
              >
                {t('directSale.mobile.keep')}
              </button>
              <button
                type="button"
                onClick={() => selectProduct(pendingSwitch.id)}
                className="h-[52px] rounded-[12px] bg-primary text-[15px] font-bold text-primary-fg"
              >
                {t('directSale.mobile.switchConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
